import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type Hero } from '../lib/api.ts';

/**
 * Hero imagery.
 *
 * Icons come from Valve's CDN rather than being mirrored locally: there are 127
 * of them, the set grows whenever a hero is released, and the path is derived
 * from the internal hero name which the API already exposes as a slug. The 4.7 KB
 * `icons/` variant is used for tables — the full portrait is 62 KB and would
 * cost ~3 MB on a page showing fifty rows.
 *
 * Rank badges ARE mirrored locally (see RankBadge): a fixed set of thirteen, and
 * the only public source for them is another site's asset directory rather than
 * a documented endpoint.
 */

const HERO_ICON_BASE = 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/dota_react/heroes';

interface HeroLookup {
  byId: Map<number, Hero>;
  name: (heroId: number) => string;
  slug: (heroId: number) => string | null;
}

const HeroContext = createContext<HeroLookup | null>(null);

/**
 * Loads the hero list once for the whole app.
 *
 * Previously every table row ran its own `useQuery` for the full hero list;
 * React Query deduplicated the network call but each row still re-rendered on
 * the shared result and did its own linear `find` over 127 heroes.
 */
export function HeroProvider({ children }: { children: ReactNode }) {
  const { data } = useQuery({
    queryKey: ['heroes'],
    queryFn: api.heroes,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
  });

  const value = useMemo<HeroLookup>(() => {
    const byId = new Map((data?.heroes ?? []).map((h) => [h.id, h]));
    return {
      byId,
      name: (heroId) => byId.get(heroId)?.name ?? `Hero ${heroId}`,
      slug: (heroId) => byId.get(heroId)?.slug ?? null,
    };
  }, [data]);

  return <HeroContext.Provider value={value}>{children}</HeroContext.Provider>;
}

export function useHeroes(): HeroLookup {
  const context = useContext(HeroContext);
  if (context) return context;
  // Usable outside the provider (tests, isolated components) without throwing.
  return {
    byId: new Map(),
    name: (heroId) => `Hero ${heroId}`,
    slug: () => null,
  };
}

export function heroIconUrl(slug: string): string {
  return `${HERO_ICON_BASE}/icons/${slug}.png`;
}

export function heroPortraitUrl(slug: string): string {
  return `${HERO_ICON_BASE}/${slug}.png`;
}

const SIZES = { sm: 20, md: 28, lg: 40 } as const;

/**
 * A hero portrait with its name.
 *
 * Falls back to text alone if the image fails, so a CDN outage degrades to the
 * previous name-only display rather than to a row of broken-image icons.
 */
export function HeroIcon({
  heroId,
  size = 'md',
  showName = true,
  className = '',
}: {
  heroId: number;
  size?: keyof typeof SIZES;
  showName?: boolean;
  className?: string;
}) {
  const heroes = useHeroes();
  const [failed, setFailed] = useState(false);

  const name = heroes.name(heroId);
  const slug = heroes.slug(heroId);
  const px = SIZES[size];

  return (
    <span className={`inline-flex min-w-0 items-center gap-2 ${className}`}>
      {slug && !failed ? (
        <img
          src={heroIconUrl(slug)}
          alt={showName ? '' : name}
          title={showName ? undefined : name}
          width={px}
          height={px}
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
          className="shrink-0 rounded-sm bg-surface-3 object-cover"
          style={{ width: px, height: px }}
        />
      ) : (
        // Reserve the same box so rows do not reflow while images load.
        <span
          aria-hidden="true"
          className="shrink-0 rounded-sm bg-surface-3"
          style={{ width: px, height: px }}
        />
      )}
      {showName && <span className="truncate">{name}</span>}
    </span>
  );
}

/** A compact row of hero portraits, e.g. a player's three most-played. */
export function HeroStack({
  heroes: usage,
  size = 'sm',
}: {
  heroes: Array<{ heroId: number; games: number }>;
  size?: keyof typeof SIZES;
}) {
  const heroes = useHeroes();
  if (usage.length === 0) return <span className="text-ink-2">—</span>;

  return (
    <span className="inline-flex items-center gap-1">
      {usage.map((entry) => (
        <span key={entry.heroId} title={`${heroes.name(entry.heroId)} — ${entry.games} games`}>
          <HeroIcon heroId={entry.heroId} size={size} showName={false} />
        </span>
      ))}
    </span>
  );
}
